import axios from "axios";
import secureAxios from "./axios/secureAxios";
import constants from "../configs/constants";

// ==================== BRAND CRUD ====================

async function listBrands() {
  const { data } = await secureAxios.get("/brand-dna");
  return data;
}

async function getBrand(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}`);
  return data;
}

async function createBrand({ brand_name, source_url }) {
  const { data } = await secureAxios.post("/brand-dna", { brand_name, source_url });
  return data;
}

async function updateBrand(brandId, updates) {
  const { data } = await secureAxios.put(`/brand-dna/${brandId}`, updates);
  return data;
}

async function deleteBrand(brandId) {
  const { data } = await secureAxios.delete(`/brand-dna/${brandId}`);
  return data;
}

async function setDefaultBrand(brandId) {
  const { data } = await secureAxios.put(`/brand-dna/${brandId}/default`);
  return data;
}

// ==================== PILLAR CRUD ====================

async function getPillar(brandId, pillarName) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/${pillarName}`);
  return data;
}

async function updatePillar(brandId, pillarName, updates) {
  const { data } = await secureAxios.put(`/brand-dna/${brandId}/${pillarName}`, updates);
  return data;
}

// ==================== PRODUCTS (now via brand-media groups) ====================

async function listProducts(brandId) {
  const { data } = await secureAxios.get(`/brand-media/${brandId}/groups`);
  return data;
}

async function createProduct(brandId, product) {
  const { data } = await secureAxios.post(`/brand-media/${brandId}/groups`, product);
  return data;
}

async function updateProduct(brandId, productId, updates) {
  const { data } = await secureAxios.patch(`/brand-media/${brandId}/groups/${productId}`, updates);
  return data;
}

async function deleteProduct(brandId, productId) {
  const { data } = await secureAxios.delete(`/brand-media/${brandId}/groups/${productId}`);
  return data;
}

// ==================== ASSETS ====================

async function listAssets(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/assets`);
  return data;
}

async function createAsset(brandId, asset) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/assets`, asset);
  return data;
}

async function deleteAsset(brandId, assetId) {
  const { data } = await secureAxios.delete(`/brand-dna/${brandId}/assets/${assetId}`);
  return data;
}

// ==================== MOODBOARD ====================

async function listMoodboard(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/moodboard`);
  return data;
}

async function createMoodboardItem(brandId, item) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/moodboard`, item);
  return data;
}

async function deleteMoodboardItem(brandId, moodboardId) {
  const { data } = await secureAxios.delete(`/brand-dna/${brandId}/moodboard/${moodboardId}`);
  return data;
}

// ==================== COMPETITORS ====================

async function listCompetitors(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/competitors`);
  return data;
}

async function createCompetitor(brandId, competitor) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/competitors`, competitor);
  return data;
}

async function updateCompetitor(brandId, competitorId, data) {
  const { data: res } = await secureAxios.put(`/brand-dna/${brandId}/competitors/${competitorId}`, data);
  return res;
}

async function deleteCompetitor(brandId, competitorId) {
  const { data } = await secureAxios.delete(`/brand-dna/${brandId}/competitors/${competitorId}`);
  return data;
}

// ==================== BRAND ASSET LIBRARY ====================

async function listBrandAssets(brandId) {
  const { data } = await secureAxios.get(`/brand-assets/${brandId}/assets`);
  return data;
}

async function uploadBrandAsset(brandId, file, assetType) {
  // Step 1: upload file to MinIO via the existing image-upload endpoint
  const formData = new FormData();
  formData.append('image', file);
  const uploadRes = await secureAxios.post('/image-upload/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  if (!uploadRes.data?.image_url) {
    throw new Error('Upload failed — no URL returned');
  }

  // Step 2: register the asset in the brand_asset table
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/brand-assets`, {
    asset_type: assetType || 'other',
    external_url: uploadRes.data.image_url,
    file_name: file.name || null,
    file_size: file.size || null,
  });

  return data;
}

async function deleteBrandAsset(brandId, assetId) {
  const { data } = await secureAxios.delete(`/brand-dna/${brandId}/brand-assets/${assetId}`);
  return data;
}

async function generateAssetCaption(brandId, assetId) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/brand-assets/${assetId}/caption`);
  return data;
}

// ==================== SOCIAL SCRAPING ====================

async function scrapeSocial(brandId, platform, handle) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/scrape-social`, { platform, handle });
  return data;
}

// ==================== EXTRACTION ====================

async function startExtraction(brandId, source_url) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/extract`, { source_url });
  return data;
}

async function getExtractionStatus(brandId, extractionId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/extract/${extractionId}`);
  return data;
}

// ==================== PROMPT ====================

async function getBrandPrompt(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/prompt`);
  return data;
}

// ==================== COLLABORATORS ====================

async function listCollaborators(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/collaborators`);
  return data;
}

async function listBrandInvitations(brandId) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/collaborators/invitations`);
  return data;
}

async function sendBrandInvitation(brandId, email) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/collaborators/invite`, { email });
  return data;
}

async function cancelBrandInvitation(brandId, invitationId) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/collaborators/invitations/${invitationId}/cancel`);
  return data;
}

async function resendBrandInvitation(brandId, invitationId) {
  const { data } = await secureAxios.post(`/brand-dna/${brandId}/collaborators/invitations/${invitationId}/resend`);
  return data;
}

async function removeCollaborator(brandId, collaboratorClientId) {
  const { data } = await secureAxios.delete(`/brand-dna/${brandId}/collaborators/${collaboratorClientId}`);
  return data;
}

async function transferBrand(brandId, email) {
  const { data } = await secureAxios.put(`/brand-dna/${brandId}/transfer`, { email });
  return data;
}

// ==================== BRAND INVITATION (public token lookup) ====================

async function getBrandInvitationByToken(token) {
  const { data } = await axios.get(`${constants.API_URL}/brand-invitations/token/${token}`);
  return data;
}

async function acceptBrandInvitation(token) {
  const { data } = await secureAxios.post('/brand-invitations/accept', { token });
  return data;
}

async function rejectBrandInvitation(invitationId) {
  const { data } = await secureAxios.post(`/brand-invitations/${invitationId}/reject`);
  return data;
}

// ==================== GENERATIONS ====================

async function getBrandGenerations(brandId, { page = 1, limit = 20 } = {}) {
  const { data } = await secureAxios.get(`/brand-dna/${brandId}/generations`, { params: { page, limit } });
  return data;
}

export const brandDnaActions = {
  listBrands,
  getBrand,
  createBrand,
  updateBrand,
  deleteBrand,
  setDefaultBrand,
  getPillar,
  updatePillar,
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  listAssets,
  createAsset,
  deleteAsset,
  listBrandAssets,
  uploadBrandAsset,
  deleteBrandAsset,
  generateAssetCaption,
  listMoodboard,
  createMoodboardItem,
  deleteMoodboardItem,
  listCompetitors,
  createCompetitor,
  updateCompetitor,
  deleteCompetitor,
  scrapeSocial,
  startExtraction,
  getExtractionStatus,
  getBrandPrompt,
  listCollaborators,
  listBrandInvitations,
  sendBrandInvitation,
  cancelBrandInvitation,
  resendBrandInvitation,
  removeCollaborator,
  transferBrand,
  getBrandInvitationByToken,
  acceptBrandInvitation,
  rejectBrandInvitation,
  getBrandGenerations,
};

export default brandDnaActions;
